import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, TextField, Button, Stack,
  Skeleton, Divider, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Tooltip, Badge,
  Checkbox, Radio, FormControlLabel, LinearProgress, Switch, Chip
} from '@mui/material'
import { Edit, Delete, ArrowBack, MarkEmailRead, Poll as PollIcon, Assignment as SignUpIcon, Add } from '@mui/icons-material'
import UserNameChip from '@/components/UserNameChip'
import MediaUploader, { UploadedMedia } from '@/components/MediaUploader'
import MediaDisplay from '@/components/MediaDisplay'
import EmojiReactions from '@/components/EmojiReactions'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import { DiscussionDetail, DiscussionComment, Poll, SignUpSheet } from '@/types'
import type { ReactionGroup } from '@/types'
import { useGroupHub } from '@/hooks/useGroupHub'
import { useGroupNavigation } from '@/contexts/GroupNavigationContext'
import { buildFamilyColorMap, getFamilyColor } from '@/utils/familyColors'

export default function DiscussionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [discussion, setDiscussion] = useState<DiscussionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [commentMedia, setCommentMedia] = useState<UploadedMedia[]>([])
  const [submittingComment, setSubmittingComment] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [, setIsMarkedUnread] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const { adjustDiscussionBadgeCount } = useGroupNavigation()
  const [editMedia, setEditMedia] = useState<UploadedMedia[]>([])

  // Poll edit state
  const [editPollOptions, setEditPollOptions] = useState<{ id?: string; text: string }[]>([])
  const [editPollAllowMultiple, setEditPollAllowMultiple] = useState(false)

  // Poll state
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())
  const [voteForFamily, setVoteForFamily] = useState(false)
  const [submittingVote, setSubmittingVote] = useState(false)

  // Sign-up sheet state
  const [signUpItemIds, setSignUpItemIds] = useState<Set<string>>(new Set())
  const [submittingSignUp, setSubmittingSignUp] = useState(false)
  const [newItemText, setNewItemText] = useState('')
  const [addingItem, setAddingItem] = useState(false)

  const loadData = useCallback(async () => {
    if (!id) return
    try {
      const data = await apiClient.get<DiscussionDetail>(`/api/discussions/${id}`)
      setDiscussion(data)
      setIsMarkedUnread(data.isMarkedUnread)
      setIsNew(data.isNew)
      // Auto-mark as viewed for genuinely new items (not explicitly marked unread)
      if (data.isNew && !data.isMarkedUnread) {
        try {
          await apiClient.post(`/api/discussions/${id}/viewed`)
          setIsNew(false)
          adjustDiscussionBadgeCount(-1)
        } catch {
          // Silently ignore - view tracking is best-effort
        }
      }
    } catch {
      enqueueSnackbar('Failed to load discussion', { variant: 'error' })
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }, [id, enqueueSnackbar, navigate, adjustDiscussionBadgeCount])

  useEffect(() => { loadData() }, [loadData])

  // Sync selected options when poll data loads or votes change
  const pollVotedIds = discussion?.poll?.currentUserVotedOptionIds
  useEffect(() => {
    if (pollVotedIds) {
      setSelectedOptions(new Set(pollVotedIds))
    }
  }, [pollVotedIds])

  // Sync sign-up selections when sheet data loads
  const signUpItems = discussion?.signUpSheet?.items
  useEffect(() => {
    if (signUpItems && user) {
      const myItemIds = signUpItems
        .filter(i => i.signedUpUsers.some(u => u.userId === user.userId))
        .map(i => i.id)
      setSignUpItemIds(new Set(myItemIds))
    }
  }, [signUpItems, user])

  const handleVote = async () => {
    if (!id || !discussion?.poll) return
    setSubmittingVote(true)
    try {
      const updatedPoll = await apiClient.post<Poll>(`/api/discussions/${id}/vote`, {
        optionIds: Array.from(selectedOptions),
        voteForFamily: voteForFamily,
      })
      setDiscussion(prev => prev ? { ...prev, poll: updatedPoll } : prev)
      setSelectedOptions(new Set(updatedPoll.currentUserVotedOptionIds))
    } catch {
      enqueueSnackbar('Failed to cast vote', { variant: 'error' })
    } finally {
      setSubmittingVote(false)
    }
  }

  const handleOptionToggle = (optionId: string) => {
    if (!discussion?.poll) return
    if (discussion.poll.allowMultipleSelections) {
      setSelectedOptions(prev => {
        const next = new Set(prev)
        if (next.has(optionId)) next.delete(optionId)
        else next.add(optionId)
        return next
      })
    } else {
      setSelectedOptions(new Set([optionId]))
    }
  }

  const handleSignUpToggle = (itemId: string) => {
    setSignUpItemIds(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  const handleSignUpSubmit = async () => {
    if (!id) return
    setSubmittingSignUp(true)
    try {
      const updatedSheet = await apiClient.post<SignUpSheet>(`/api/discussions/${id}/signup`, {
        itemIds: Array.from(signUpItemIds),
      })
      setDiscussion(prev => prev ? { ...prev, signUpSheet: updatedSheet } : prev)
    } catch {
      enqueueSnackbar('Failed to update sign-up', { variant: 'error' })
    } finally {
      setSubmittingSignUp(false)
    }
  }

  const handleRemoveSignUp = async (itemId: string) => {
    if (!id) return
    try {
      const updatedSheet = await apiClient.delete<SignUpSheet>(`/api/discussions/${id}/signup/${itemId}`)
      setDiscussion(prev => prev ? { ...prev, signUpSheet: updatedSheet } : prev)
      setSignUpItemIds(prev => { const n = new Set(prev); n.delete(itemId); return n })
    } catch {
      enqueueSnackbar('Failed to remove sign-up', { variant: 'error' })
    }
  }

  const handleAddSignUpItem = async () => {
    if (!id || !newItemText.trim()) return
    setAddingItem(true)
    try {
      const updatedSheet = await apiClient.post<SignUpSheet>(`/api/discussions/${id}/signup/items`, {
        text: newItemText.trim(),
      })
      setDiscussion(prev => prev ? { ...prev, signUpSheet: updatedSheet } : prev)
      setNewItemText('')
    } catch {
      enqueueSnackbar('Failed to add item', { variant: 'error' })
    } finally {
      setAddingItem(false)
    }
  }

  const handleDeleteSignUpItem = async (itemId: string) => {
    if (!id) return
    try {
      const updatedSheet = await apiClient.delete<SignUpSheet>(`/api/discussions/${id}/signup/items/${itemId}`)
      setDiscussion(prev => prev ? { ...prev, signUpSheet: updatedSheet } : prev)
      setSignUpItemIds(prev => { const n = new Set(prev); n.delete(itemId); return n })
    } catch {
      enqueueSnackbar('Failed to delete item', { variant: 'error' })
    }
  }

  // Real-time updates via SignalR
  const groupIdsForHub = useMemo(() => discussion?.groupId ? [discussion.groupId] : [], [discussion?.groupId])
  useGroupHub(groupIdsForHub, {
    onDiscussionCommentAdded: (data) => {
      if (data.discussionId !== id) return
      setDiscussion(prev => {
        if (!prev) return prev
        if (prev.comments.some(c => c.id === data.comment.id)) return prev
        return { ...prev, comments: [...prev.comments, data.comment] }
      })
    },
    onDiscussionUpdated: (updated) => {
      if (updated.id !== id) return
      setDiscussion(prev => prev ? {
        ...prev,
        title: updated.title,
        description: updated.description,
      } : prev)
    },
    onReactionToggled: (data) => {
      setDiscussion(prev => {
        if (!prev) return prev
        if (data.entityId === prev.id) {
          return { ...prev, reactions: data.reactions }
        }
        const commentIndex = prev.comments.findIndex(c => c.id === data.entityId)
        if (commentIndex >= 0) {
          const updatedComments = [...prev.comments]
          updatedComments[commentIndex] = { ...updatedComments[commentIndex], reactions: data.reactions }
          return { ...prev, comments: updatedComments }
        }
        return prev
      })
    },
  })

  const handleAddComment = async () => {
    if (!commentText.trim() || !id) return
    setSubmittingComment(true)
    try {
      const comment = await apiClient.post<DiscussionComment>(`/api/discussions/${id}/comments`, {
        text: commentText.trim(),
        mediaIds: commentMedia.map(u => u.id),
      })
      setDiscussion(prev => {
        if (!prev) return null
        if (prev.comments.some(c => c.id === comment.id)) return prev
        return { ...prev, comments: [...prev.comments, comment] }
      })
      setCommentText('')
      // Backend auto-marks as viewed on comment; update local state
      if (isNew) {
        setIsNew(false)
        setIsMarkedUnread(false)
        adjustDiscussionBadgeCount(-1)
      }
      setCommentMedia([])
    } catch {
      enqueueSnackbar('Failed to add comment', { variant: 'error' })
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleUpdate = async () => {
    if (!id || !editTitle.trim() || !editDescription.trim()) return
    const validPollOptions = editPollOptions.filter(o => o.text.trim().length > 0)
    if (discussion?.poll && validPollOptions.length < 2) return
    try {
      await apiClient.put(`/api/discussions/${id}`, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        mediaIds: editMedia.map(u => u.id),
      })
      setDiscussion(prev => prev ? {
        ...prev,
        title: editTitle.trim(),
        description: editDescription.trim()
      } : null)
      if (discussion?.poll) {
        const updatedPoll = await apiClient.put<Poll>(`/api/discussions/${id}/poll`, {
          allowMultipleSelections: editPollAllowMultiple,
          options: editPollOptions
            .filter(o => o.text.trim().length > 0)
            .map(o => ({ id: o.id ?? null, text: o.text.trim() })),
        })
        setDiscussion(prev => prev ? { ...prev, poll: updatedPoll } : null)
      }
      setEditOpen(false)
      enqueueSnackbar('Discussion updated', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to update', { variant: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!id || !discussion) return
    if (!confirm('Are you sure you want to delete this discussion?')) return
    try {
      await apiClient.delete(`/api/discussions/${id}`)
      enqueueSnackbar('Discussion deleted', { variant: 'success' })
      navigate(`/groups/${discussion.groupId}`)
    } catch {
      enqueueSnackbar('Failed to delete', { variant: 'error' })
    }
  }

  const handleToggleUnread = async () => {
    if (!id) return
    try {
      if (isNew) {
        // Mark as viewed (explicit action)
        await apiClient.post(`/api/discussions/${id}/viewed`)
        setIsNew(false)
        setIsMarkedUnread(false)
        adjustDiscussionBadgeCount(-1)
        enqueueSnackbar('Marked as viewed', { variant: 'success' })
      } else {
        // Mark as unread
        await apiClient.post(`/api/discussions/${id}/unread`)
        setIsNew(true)
        setIsMarkedUnread(true)
        adjustDiscussionBadgeCount(1)
        enqueueSnackbar('Marked as unread', { variant: 'success' })
      }
    } catch {
      enqueueSnackbar('Failed to update read status', { variant: 'error' })
    }
  }

  const isOwner = user?.userId === discussion?.userId
    || (user?.familyId != null && user.familyId === discussion?.familyId)

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="70%" height={40} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={60} />
      </Box>
    )
  }

  if (!discussion) return null

  // Build color map from all family/user IDs on this page (author + commenters)
  const allIds = [discussion.familyId ?? discussion.userId, ...discussion.comments.map(c => c.familyId ?? c.userId)]
  const colorMap = buildFamilyColorMap(allIds)
  const authorColor = getFamilyColor(colorMap, discussion.familyId ?? discussion.userId)

  return (
    <Box sx={{ pb: 10, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(`/groups/${discussion.groupId}`)} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1, fontWeight: 700 }}>
          {discussion.title}
        </Typography>
        <Tooltip title={isNew ? 'Mark as viewed' : 'Mark as unread'}>
          <IconButton onClick={handleToggleUnread} sx={{ color: isNew ? 'success.main' : 'action.disabled' }}>
            {isNew ? (
              <Badge variant="dot" color="error" overlap="circular"
                sx={{ '& .MuiBadge-badge': { minWidth: 10, height: 10, borderRadius: '50%', top: 2, right: 2 } }}>
                <MarkEmailRead />
              </Badge>
            ) : (
              <MarkEmailRead />
            )}
          </IconButton>
        </Tooltip>
        {isOwner && (
          <Box>
            <IconButton onClick={() => {
              setEditTitle(discussion.title)
              setEditDescription(discussion.description)
              setEditMedia(
                (discussion.mediaAttachments ?? []).map(a => ({
                  id: a.id,
                  url: a.url,
                  fileName: a.fileName,
                  contentType: a.contentType,
                }))
              )
              if (discussion.poll) {
                setEditPollAllowMultiple(discussion.poll.allowMultipleSelections)
                setEditPollOptions(discussion.poll.options.map(o => ({ id: o.id, text: o.text })))
              }
              setEditOpen(true)
            }}>
              <Edit />
            </IconButton>
            <IconButton color="error" onClick={handleDelete}>
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{discussion.description}</Typography>
          {discussion.mediaAttachments && discussion.mediaAttachments.length > 0 && (
            <MediaDisplay attachments={discussion.mediaAttachments} />
          )}
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
            <UserNameChip
              userId={discussion.userId}
              displayName={discussion.userDisplayName}
              bgColor={authorColor?.bg}
              textColor={authorColor?.text}
            />
            <Typography variant="caption" color="text.secondary">
              {new Date(discussion.createdDate).toLocaleDateString()}
            </Typography>
          </Box>
          <EmojiReactions
            entityId={discussion.id}
            entityType="discussion"
            reactions={discussion.reactions}
            onReactionsChange={(reactions: ReactionGroup[]) => setDiscussion(prev => prev ? { ...prev, reactions } : null)}
          />
        </CardContent>
      </Card>

      {/* Poll */}
      {discussion.poll && (() => {
        const totalVotes = discussion.poll.options.reduce((sum, o) => sum + o.voteCount, 0)
        const hasVoted = discussion.poll.currentUserVotedOptionIds.length > 0
        return (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <PollIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Poll {discussion.poll.allowMultipleSelections ? '(select multiple)' : '(select one)'}
                </Typography>
              </Box>

              {/* Results bar graph */}
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {discussion.poll.options.map(option => {
                  const pct = totalVotes > 0 ? (option.voteCount / totalVotes) * 100 : 0
                  const isSelected = selectedOptions.has(option.id)
                  const votedBy = option.voterDisplayNames
                  return (
                    <Box key={option.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 400 }}>
                          {option.text}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.voteCount} vote{option.voteCount !== 1 ? 's' : ''} ({Math.round(pct)}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          bgcolor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            bgcolor: isSelected ? 'primary.main' : 'grey.400',
                          },
                        }}
                      />
                      {votedBy.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Voted by: {votedBy.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {totalVotes} total vote{totalVotes !== 1 ? 's' : ''}
              </Typography>

              {/* Voting controls */}
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {hasVoted ? 'Change your vote' : 'Cast your vote'}
              </Typography>
              <Stack spacing={0.5}>
                {discussion.poll.options.map(option => (
                  <FormControlLabel
                    key={option.id}
                    control={
                      discussion.poll?.allowMultipleSelections
                        ? <Checkbox
                            checked={selectedOptions.has(option.id)}
                            onChange={() => handleOptionToggle(option.id)}
                            size="small"
                          />
                        : <Radio
                            checked={selectedOptions.has(option.id)}
                            onChange={() => handleOptionToggle(option.id)}
                            size="small"
                          />
                    }
                    label={<Typography variant="body2">{option.text}</Typography>}
                  />
                ))}
              </Stack>

              {user?.familyId && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={voteForFamily}
                      onChange={e => setVoteForFamily(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Vote for my entire family</Typography>}
                  sx={{ mt: 1 }}
                />
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleVote}
                disabled={submittingVote}
                sx={{ mt: 1 }}
              >
                {submittingVote ? 'Voting...' : hasVoted ? 'Update Vote' : 'Vote'}
              </Button>
            </CardContent>
          </Card>
        )
      })()}

      {/* Sign-Up Sheet */}
      {discussion.signUpSheet && (() => {
        const sheet = discussion.signUpSheet!
        return (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <SignUpIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sign-Up Sheet</Typography>
              </Box>

              <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                {sheet.items.map(item => {
                  const isSignedUp = item.signedUpUsers.some(u => u.userId === user?.userId)
                  const isSelected = signUpItemIds.has(item.id)
                  const canDeleteItem = item.addedByUserId === user?.userId
                    || (item.addedByUserId === null && discussion.userId === user?.userId)
                  return (
                    <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSignUpToggle(item.id)}
                          size="small"
                          sx={{ mt: -0.5, ml: -0.5 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.text}</Typography>
                          {item.signedUpUsers.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {item.signedUpUsers.map(u => (
                                <Chip
                                  key={u.userId}
                                  label={u.displayName}
                                  size="small"
                                  variant="outlined"
                                  onDelete={u.userId === user?.userId ? () => handleRemoveSignUp(item.id) : undefined}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                        {canDeleteItem && (
                          <IconButton size="small" color="error" onClick={() => handleDeleteSignUpItem(item.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      {isSignedUp && !isSelected && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                          Uncheck and save to remove your sign-up.
                        </Typography>
                      )}
                    </Paper>
                  )
                })}
              </Stack>

              <Button
                variant="contained"
                size="small"
                onClick={handleSignUpSubmit}
                disabled={submittingSignUp}
              >
                {submittingSignUp ? 'Saving...' : 'Save Sign-Ups'}
              </Button>

              {sheet.allowAddItems && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add a new item..."
                    value={newItemText}
                    onChange={e => setNewItemText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddSignUpItem())}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                    disabled={addingItem}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddSignUpItem}
                    disabled={addingItem || !newItemText.trim()}
                  >
                    Add
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )
      })()}

      <Divider sx={{ my: 2 }} />

      {/* Comments */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Comments ({discussion.comments.length})
      </Typography>

      {discussion.comments.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {discussion.comments.map(comment => {
            const commentColor = getFamilyColor(colorMap, comment.familyId ?? comment.userId)
            return (
            <Paper key={comment.id} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment.text}</Typography>
              {comment.mediaAttachments && comment.mediaAttachments.length > 0 && (
                <MediaDisplay attachments={comment.mediaAttachments} />
              )}
              <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                <UserNameChip
                  userId={comment.userId}
                  displayName={comment.userDisplayName}
                  bgColor={commentColor?.bg}
                  textColor={commentColor?.text}
                />
                <Typography variant="caption" color="text.secondary">
                  {new Date(comment.createdDate).toLocaleDateString()}
                </Typography>
              </Box>
              <EmojiReactions
                entityId={comment.id}
                entityType="discussioncomment"
                reactions={comment.reactions}
                onReactionsChange={(reactions: ReactionGroup[]) => setDiscussion(prev => {
                  if (!prev) return null
                  return { ...prev, comments: prev.comments.map(c => c.id === comment.id ? { ...c, reactions } : c) }
                })}
              />
            </Paper>
            )
          })}
        </Stack>
      )}

      {/* Add Comment */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Add a comment..."
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleAddComment())}
            multiline
            maxRows={3}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={submittingComment || !commentText.trim()}
            sx={{ minWidth: 80 }}
          >
            Send
          </Button>
        </Box>
        <MediaUploader
          uploads={commentMedia}
          onChange={setCommentMedia}
          disabled={submittingComment}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Discussion</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Title" value={editTitle}
            onChange={e => setEditTitle(e.target.value)} margin="dense"
          />
          <TextField
            fullWidth label="Description" value={editDescription}
            onChange={e => setEditDescription(e.target.value)} margin="dense"
            multiline rows={3}
          />
          <Box sx={{ mt: 1 }}>
            <MediaUploader uploads={editMedia} onChange={setEditMedia} />
          </Box>

          {/* Poll editing */}
          {discussion?.poll && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PollIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">Poll Options</Typography>
              </Box>
              {editPollOptions.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Option ${index + 1}`}
                    value={option.text}
                    onChange={e => {
                      const updated = [...editPollOptions]
                      updated[index] = { ...updated[index], text: e.target.value }
                      setEditPollOptions(updated)
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  {editPollOptions.length > 2 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setEditPollOptions(editPollOptions.filter((_, i) => i !== index))}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => setEditPollOptions([...editPollOptions, { text: '' }])}
              >
                Add Option
              </Button>
              {editPollOptions.filter(o => o.text.trim().length > 0).length < 2 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  A poll requires at least two non-empty options.
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={editPollAllowMultiple}
                    onChange={e => setEditPollAllowMultiple(e.target.checked)}
                    size="small"
                  />
                }
                label="Allow selecting multiple options"
                sx={{ mt: 1, display: 'block' }}
              />
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={
              !editTitle.trim() || !editDescription.trim() ||
              (!!discussion?.poll && editPollOptions.filter(o => o.text.trim().length > 0).length < 2)
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

